/**
 * Taking a backup, and putting one back (T-502).
 *
 * The format itself lives in `serialise/backup.ts`. This is the part that
 * touches storage: reading everything out, working out what an import would
 * change *before* it changes anything, and applying it atomically.
 *
 * The shape follows the rule the repositories already established — a
 * destructive action whose outcome depends on state returns a **plan** the
 * caller can read before confirming. Restoring a backup is the most destructive
 * thing this app can do, and "Import?" followed by a silently emptied database
 * is the broken promise that matters most.
 */

import { err, invalid, ok, type Result } from '../model/result.js'
import type { Timestamp } from '../model/timestamp.js'
import type { Clock } from '../ports/clock.js'
import type { ExportFile } from '../ports/exporter.js'
import type { Storage } from '../ports/storage.js'
import {
  backupToFile,
  makeBackup,
  type Backup,
  type BackupCounts,
  type BackupData,
} from '../serialise/backup.js'
import { BACKUP_DISMISSED_KEY, LAST_BACKUP_KEY } from './backup-health.js'

/**
 * The pre-import copy of the database, kept where a reload cannot lose it.
 *
 * Regret about a restore does not arrive while the progress bar is on screen;
 * it arrives ten minutes later. Holding the copy in memory would mean the one
 * thing that could undo the mistake vanishes with the tab.
 */
export const SAFETY_BACKUP_KEY = 'anytracker.safetyBackup'

/**
 * Settings deliberately left out of a backup.
 *
 * All three are bookkeeping *about* backups rather than data being tracked, and
 * each is wrong to copy for its own reason:
 *
 *   - The safety copy is itself a whole database. Including it would put a copy
 *     of yesterday's database inside today's backup, and a copy of that inside
 *     tomorrow's — the file doubling every time until it is unusable.
 *   - The last-backup record is a fact about a *device*, and after a restore
 *     the correct value is derived from the file that was just imported, not
 *     copied out of it. It is stamped below.
 *   - A dismissed prompt is one person's decision on one device. Carrying it
 *     across would silence a warning on a device that has never been backed up.
 */
const NOT_BACKED_UP: readonly string[] = [
  SAFETY_BACKUP_KEY,
  LAST_BACKUP_KEY,
  BACKUP_DISMISSED_KEY,
]

export type ImportMode = 'replace' | 'merge' | 'inspect'

export interface EntityChange {
  readonly inFile: number
  readonly adding: number
  /** Present in both. Kept as they are by merge, overwritten by replace. */
  readonly overlapping: number
  /** Here but not in the file. Removed only by replace. */
  readonly removing: number
}

/**
 * A reference that will not resolve once the import is done.
 *
 * Reported rather than refused. A backup is the only recovery path there is, so
 * losing a measurement because the track it names is missing would be a strictly
 * worse outcome than keeping it and saying so — and in merge mode the track may
 * already be here, which is why this is computed against the state *after* the
 * import rather than against the file alone.
 */
export interface DanglingReference {
  readonly kind: 'track' | 'tag' | 'check-in'
  readonly id: string
  readonly affected: number
}

export interface ImportPlan {
  readonly mode: ImportMode
  readonly exportedAt: Timestamp
  readonly appVersion: string
  readonly schemaVersion: number
  readonly tracks: EntityChange
  readonly entries: EntityChange
  readonly tags: EntityChange
  readonly templates: EntityChange
  readonly settings: EntityChange
  readonly dangling: readonly DanglingReference[]
}

export interface ImportProgress {
  readonly step: 'safety' | 'clearing' | 'writing' | 'done'
  /** Records written so far, of the total this import will write. */
  readonly done: number
  readonly total: number
}

export interface ImportDeps {
  /**
   * Stamps the safety copy.
   *
   * Without it the copy taken before a replace inherits the *imported* file's
   * export time, so a folder of backups would carry two files claiming to be
   * from the same moment — and the one that matters, the state being replaced,
   * would be the one lying about its age.
   */
  readonly clock: Clock
  readonly onProgress?: (progress: ImportProgress) => void
}

export interface ImportOutcome {
  readonly plan: ImportPlan
  readonly written: BackupCounts
  /**
   * The database as it was, taken before a replace.
   *
   * Absent for merge — which adds and never removes — and for a replace into an
   * empty database, where there is nothing to protect.
   */
  readonly safetyCopy?: ExportFile
}

/** Entries written per `putMany`, so progress means something on a large file. */
const WRITE_CHUNK = 500

// ─── Taking one ───────────────────────────────────────────────────────────────

/** Everything, read out of storage. */
export async function collectBackupData(storage: Storage): Promise<BackupData> {
  const [tracks, entries, tags, templates, settings] = await Promise.all([
    storage.tracks.list(),
    storage.entries.list(),
    storage.tags.list(),
    storage.templates.list(),
    storage.settings.all(),
  ])

  return {
    tracks,
    entries,
    tags,
    templates,
    settings: Object.fromEntries(
      Object.entries(settings).filter(([key]) => !NOT_BACKED_UP.includes(key)),
    ),
  }
}

export async function collectBackup(storage: Storage, clock: Clock): Promise<Backup> {
  return makeBackup(await collectBackupData(storage), clock)
}

export async function exportBackup(
  storage: Storage,
  clock: Clock,
): Promise<{ readonly file: ExportFile; readonly backup: Backup }> {
  const backup = await collectBackup(storage, clock)
  return { file: backupToFile(backup), backup }
}

/** The safety copy taken before the last replace import, if there is one. */
export function readSafetyBackup(storage: Storage): Promise<string | undefined> {
  return storage.settings.get(SAFETY_BACKUP_KEY)
}

// ─── Planning ─────────────────────────────────────────────────────────────────

function changeFor(
  inFile: readonly { readonly id: string }[],
  existing: readonly { readonly id: string }[],
  mode: ImportMode,
): EntityChange {
  const fileIds = new Set(inFile.map((item) => item.id))
  const existingIds = new Set(existing.map((item) => item.id))
  const overlapping = [...fileIds].filter((id) => existingIds.has(id)).length

  return {
    inFile: fileIds.size,
    adding: fileIds.size - overlapping,
    overlapping,
    // Merge never removes anything. That is the entire distinction between the
    // two modes, and it is what makes merge safe to try first.
    removing:
      mode === 'replace' ? [...existingIds].filter((id) => !fileIds.has(id)).length : 0,
  }
}

function settingsChange(
  inFile: Readonly<Record<string, string>>,
  existing: Readonly<Record<string, string>>,
  mode: ImportMode,
): EntityChange {
  return changeFor(
    Object.keys(inFile).map((id) => ({ id })),
    Object.keys(existing)
      .filter((key) => !NOT_BACKED_UP.includes(key))
      .map((id) => ({ id })),
    mode,
  )
}

/** Counts references that will not resolve once this import has been applied. */
function danglingAfter(data: BackupData): DanglingReference[] {
  const trackIds = new Set(data.tracks.map((track) => track.id))
  const tagIds = new Set(data.tags.map((tag) => tag.id))
  const templateIds = new Set(data.templates.map((template) => template.id))

  const counts = new Map<string, DanglingReference>()
  const note = (kind: DanglingReference['kind'], id: string) => {
    const key = `${kind}:${id}`
    const seen = counts.get(key)
    counts.set(
      key,
      seen === undefined
        ? { kind, id, affected: 1 }
        : { ...seen, affected: seen.affected + 1 },
    )
  }

  for (const entry of data.entries) {
    for (const measurement of entry.measurements) {
      if (!trackIds.has(measurement.trackId)) {
        note('track', measurement.trackId)
      }
    }
    for (const tagId of entry.tagIds) {
      if (!tagIds.has(tagId)) {
        note('tag', tagId)
      }
    }
    if (entry.templateId !== undefined && !templateIds.has(entry.templateId)) {
      note('check-in', entry.templateId)
    }
  }
  for (const template of data.templates) {
    for (const trackId of template.trackIds) {
      if (!trackIds.has(trackId)) {
        note('track', trackId)
      }
    }
  }

  return [...counts.values()].sort((a, b) => b.affected - a.affected)
}

/** The state this import would leave behind, without leaving it behind. */
function projected(file: BackupData, existing: BackupData, mode: ImportMode): BackupData {
  if (mode === 'replace') {
    return file
  }
  // Merge adds what is missing, keyed on id, and never overwrites. Existing
  // records win, so a device that has been used since the backup was taken does
  // not have its newer edits replaced by older ones.
  const keepById = <T extends { readonly id: string }>(
    mine: readonly T[],
    theirs: readonly T[],
  ): T[] => {
    const ids = new Set(mine.map((item) => item.id))
    return [...mine, ...theirs.filter((item) => !ids.has(item.id))]
  }

  return {
    tracks: keepById(existing.tracks, file.tracks),
    entries: keepById(existing.entries, file.entries),
    tags: keepById(existing.tags, file.tags),
    templates: keepById(existing.templates, file.templates),
    settings: { ...file.settings, ...existing.settings },
  }
}

export async function planImport(
  storage: Storage,
  backup: Backup,
  mode: ImportMode,
): Promise<ImportPlan> {
  const existing = await collectBackupData(storage)
  const file = backup.data

  return {
    mode,
    exportedAt: backup.exportedAt,
    appVersion: backup.appVersion,
    schemaVersion: backup.schemaVersion,
    tracks: changeFor(file.tracks, existing.tracks, mode),
    entries: changeFor(file.entries, existing.entries, mode),
    tags: changeFor(file.tags, existing.tags, mode),
    templates: changeFor(file.templates, existing.templates, mode),
    settings: settingsChange(file.settings, existing.settings, mode),
    dangling: danglingAfter(projected(file, existing, mode)),
  }
}

// ─── Applying ─────────────────────────────────────────────────────────────────

function chunk<T>(items: readonly T[], size: number): T[][] {
  const chunks: T[][] = []
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size))
  }
  return chunks
}

/**
 * Restores a backup, atomically.
 *
 * Everything that can be checked is checked before the transaction opens, so
 * the only way the write can fail is a storage failure — and a storage failure
 * throws, which is what both adapters roll back on. A domain error returned as
 * a value from inside the transaction would commit the half that had already
 * been written.
 */
export async function importBackup(
  storage: Storage,
  backup: Backup,
  mode: ImportMode,
  deps: ImportDeps,
): Promise<Result<ImportOutcome>> {
  const plan = await planImport(storage, backup, mode)

  if (mode === 'inspect') {
    // Nothing is written, and the caller gets exactly what it would have got
    // from a real import — which is the point of the mode.
    return ok({ plan, written: emptyCounts() })
  }

  const report = deps.onProgress ?? (() => undefined)
  const file = backup.data
  const total =
    file.tracks.length +
    file.entries.length +
    file.tags.length +
    file.templates.length +
    Object.keys(file.settings).length

  let safetyCopy: ExportFile | undefined
  if (mode === 'replace') {
    report({ step: 'safety', done: 0, total })
    const existing = await collectBackupData(storage)
    const anything =
      existing.tracks.length +
      existing.entries.length +
      existing.tags.length +
      existing.templates.length
    if (anything > 0) {
      // A real backup of right now, built the same way any other is — so it can
      // be imported back with no special handling, which is the only property
      // that makes it worth taking.
      safetyCopy = backupToFile(makeBackup(existing, deps.clock))
    }
  }

  try {
    await storage.transaction(async (tx) => {
      let done = 0
      const advance = (by: number) => {
        done += by
        report({ step: 'writing', done, total })
      }

      if (mode === 'replace') {
        report({ step: 'clearing', done: 0, total })
        await tx.tracks.clear()
        await tx.tags.clear()
        await tx.templates.clear()
        await tx.entries.clear()
        for (const key of Object.keys(await tx.settings.all())) {
          await tx.settings.remove(key)
        }
      }

      const existingIds = mode === 'merge' ? await existingIdSets(tx) : null
      const missing = <T extends { readonly id: string }>(
        items: readonly T[],
        seen: ReadonlySet<string> | undefined,
      ): readonly T[] =>
        seen === undefined ? items : items.filter((i) => !seen.has(i.id))

      await tx.tracks.putMany(missing(file.tracks, existingIds?.tracks))
      advance(file.tracks.length)
      await tx.tags.putMany(missing(file.tags, existingIds?.tags))
      advance(file.tags.length)
      await tx.templates.putMany(missing(file.templates, existingIds?.templates))
      advance(file.templates.length)

      // Chunked, so a ten-thousand-entry restore reports progress rather than
      // appearing to hang — a frozen UI mid-import reads as a crash, and this
      // is the one operation nobody should be tempted to interrupt.
      for (const batch of chunk(
        missing(file.entries, existingIds?.entries),
        WRITE_CHUNK,
      )) {
        await tx.entries.putMany(batch)
        advance(batch.length)
      }

      for (const [key, value] of Object.entries(file.settings)) {
        if (existingIds?.settings.has(key) === true) {
          continue
        }
        await tx.settings.set(key, value)
      }
      advance(Object.keys(file.settings).length)

      /*
       * A replace makes the imported file a faithful backup of this device, so
       * it becomes the last backup (T-503).
       *
       * Without this, restoring would leave the *file's own* stale
       * `lastBackup` value in place and the staleness prompt would start
       * shouting about a backup that is sitting in the user's downloads folder.
       * Not done for a merge: a file covering part of the data is not a backup
       * of the whole of it.
       */
      if (mode === 'replace') {
        await tx.settings.set(
          LAST_BACKUP_KEY,
          JSON.stringify({ at: backup.exportedAt, entryCount: file.entries.length }),
        )
        await tx.settings.remove(BACKUP_DISMISSED_KEY)
      }

      // Written last, and inside the transaction, so a replace cannot clear the
      // one copy that could undo it.
      if (safetyCopy !== undefined && typeof safetyCopy.content === 'string') {
        await tx.settings.set(SAFETY_BACKUP_KEY, safetyCopy.content)
      }
    })
  } catch (error) {
    // A storage failure has already been rolled back by the adapter. Reporting
    // it as a value keeps the screen able to say the database is untouched,
    // which is the only thing the user actually needs to know here.
    return err<ImportOutcome>(
      invalid(
        'backup.import.failed',
        `The restore could not be completed${error instanceof Error ? ` — ${error.message}` : ''}. Nothing has been changed.`,
      ),
    )
  }

  report({ step: 'done', done: total, total })

  return ok({
    plan,
    written: {
      tracks: plan.tracks.adding + (mode === 'replace' ? plan.tracks.overlapping : 0),
      entries: plan.entries.adding + (mode === 'replace' ? plan.entries.overlapping : 0),
      tags: plan.tags.adding + (mode === 'replace' ? plan.tags.overlapping : 0),
      templates:
        plan.templates.adding + (mode === 'replace' ? plan.templates.overlapping : 0),
      settings:
        plan.settings.adding + (mode === 'replace' ? plan.settings.overlapping : 0),
    },
    ...(safetyCopy === undefined ? {} : { safetyCopy }),
  })
}

/**
 * Sequential awaits, not `Promise.all`, because this runs inside a transaction.
 *
 * Dexie binds a transaction to the surrounding async zone and rejoins it on
 * each awaited Dexie promise. `Promise.all` returns a **native** promise, and
 * awaiting one leaves the zone — after which the writes that follow silently
 * run outside the transaction and commit when they should have rolled back.
 * Slower here, correct here.
 */
async function existingIdSets(storage: Storage): Promise<{
  readonly tracks: Set<string>
  readonly entries: Set<string>
  readonly tags: Set<string>
  readonly templates: Set<string>
  readonly settings: Set<string>
}> {
  const tracks = await storage.tracks.list()
  const entries = await storage.entries.list()
  const tags = await storage.tags.list()
  const templates = await storage.templates.list()
  const settings = await storage.settings.all()
  return {
    tracks: new Set(tracks.map((t) => t.id)),
    entries: new Set(entries.map((e) => e.id)),
    tags: new Set(tags.map((t) => t.id)),
    templates: new Set(templates.map((t) => t.id)),
    settings: new Set(Object.keys(settings)),
  }
}

function emptyCounts(): BackupCounts {
  return { tracks: 0, entries: 0, tags: 0, templates: 0, settings: 0 }
}
